import { uuidModule } from "@/modules/uuid";
import type {
  SpaceAccountNoteModelData,
  SpaceAccountNoteUpsertedSyncUpdateValue,
} from "@/store/recent-items/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { buildMarkNoteViewedModelData } from "@/store/sync/operations/recents/mark-note-viewed-model-data";
import type { IMarkNoteViewedOperation } from "@/store/sync/operations/types";
import type {
  OptimisticSyncUpdate,
  SyncCustomErrorData,
  SyncModelData,
  SyncOperationKind,
} from "@/store/sync/types";

export class MarkNoteViewedOperation extends BaseSyncOperation<IMarkNoteViewedOperation> {
  get operationKind(): SyncOperationKind {
    return "MARK_NOTE_VIEWED";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    const spaceAccountNoteObservable =
      await this.store.spaceAccountNotes.getAsync(spaceAccountNoteId);
    const existingModelData = spaceAccountNoteObservable?.modelData;

    const value: SpaceAccountNoteUpsertedSyncUpdateValue = {
      model_id: spaceAccountNoteId,
      model_kind: "SPACE_ACCOUNT_NOTE",
      model_version: spaceAccountNoteObservable?.modelVersion ?? 0,
      model_data: buildMarkNoteViewedModelData({
        existingModelData,
        isRunningRelevantNoteExtractionPipeline:
          spaceAccountNoteObservable?.isRunningRelevantNoteExtractionPipeline,
        lastRelevantNoteExtractionCompletedAt:
          spaceAccountNoteObservable?.lastRelevantNoteExtractionCompletedAt,
        lastRelevantNoteExtractionNoteModelVersion:
          spaceAccountNoteObservable?.lastRelevantNoteExtractionNoteModelVersion,
        lastRelevantNoteExtractionStartedAt:
          spaceAccountNoteObservable?.lastRelevantNoteExtractionStartedAt,
        noteId: this.payload.note_id,
        spaceAccountId,
        viewedAt: this.committedAt,
      }),
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const syncUpdate: OptimisticSyncUpdate<SpaceAccountNoteModelData> = {
      optimistic_update_id: uuidModule.generate(),

      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value,
    };
    return [syncUpdate];
  }

  async triggerRecompute() {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    await this.store.spaceAccountNotes.recompute(spaceAccountNoteId);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.ignoreError(SyncErrorHandlingType.RetryWithLimit);
  }
}
